import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { catchError, firstValueFrom, map, throwError } from 'rxjs';
import { ErrorVoidAggregationResult } from '../feature-modules/dashboard-live/models/Slim/AggregationResultModel/ErrorVoidAggregationResult';
import { LoginService } from './login.service';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { EstateService } from './estate.service';
import { SlimTransaction } from '../feature-modules/dashboard-live/models/Slim/SlimDataModel/SlimTransaction';
import { UltraSlimTransaction } from '../feature-modules/dashboard-live/models/Slim/SlimDataModel/UltraSlimTransaction';
import { SlimInPlayTable } from '../feature-modules/dashboard-live/models/Slim/SlimDataModel/SlimInPlayTable';
import { SlimFunctionKey } from '../feature-modules/dashboard-live/models/Slim/SlimDataModel/SlimFunctionKey';
import { Guid } from '../types/Guid.type';
import { SlimTransactionFunctionKey } from '../feature-modules/dashboard-live/models/Slim/SlimDataModel/SlimTransactionFunctionKey';
import { ChangesSince } from '../models/ChangesStream/ChangesSince';
import { ResumeToken } from '../models/ChangesStream/ResumeToken';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private errorFunctionKeyName: string = environment.functionKeys.error;
  private voidFunctionKeyName: string = environment.functionKeys.void;

  public errorFunctionKeyGuid: Guid | undefined;
  public voidFunctionKeyGuid: Guid | undefined;

  private DashboardEndPoint = environment.APIEndpoint +  'frontend/corporation/{corporationId}/site/{siteId}/Dashboard/';

  constructor(private loginService: LoginService, private http: HttpClient, private estateService: EstateService) {

  }

  public async getLast24HoursTransactions(): Promise<SlimTransaction[]> {
    const Now = new Date();
    const dateFrom = this.createDate(Now.getHours(), Now.getMinutes(), Now, -1)

    return await this.getApiDataPOST<SlimTransaction>("SlimTransactions", dateFrom)
  }

  public async getTransactions(TradingEndHours: number, TradingEndMinute: number): Promise<UltraSlimTransaction[]> {
    const Now = new Date();

    // check if we need to get data for more days
    const previousMonthFrom = new Date(Now.getFullYear(), Now.getMonth() - 1, 1, TradingEndHours, TradingEndMinute, 0, 0);

    let dateFrom = this.createDate(TradingEndHours, TradingEndMinute, Now, -56)

    if (dateFrom > previousMonthFrom) {
      dateFrom = previousMonthFrom
    }

    return await this.getApiDataPOST<UltraSlimTransaction>("UltraSlimTransactions", dateFrom)
  }

  public async getInPlayTables(): Promise<SlimInPlayTable[]> {
    return await this.getApiDataGET<SlimInPlayTable>("SlimInPlayTables")
  }

  public async getErrors_Voids(TradingEndHours: number, TradingEndMinute: number): Promise<ErrorVoidAggregationResult> {
    const keys: SlimFunctionKey[] = await this.getError_VoidFunctionKeys()

    this.voidFunctionKeyGuid = keys.find(x => x.name == this.voidFunctionKeyName)?.functionKey_GUID;
    this.errorFunctionKeyGuid = keys.find(x => x.name == this.errorFunctionKeyName)?.functionKey_GUID;


    const [voidTransactionFunctionKeys, errorTransactionFunctionKeys] = await Promise.all([
      this.voidFunctionKeyGuid  ? this.getTransactionFunctionKeys(this.voidFunctionKeyGuid,  TradingEndHours, TradingEndMinute) : Promise.resolve([]),
      this.errorFunctionKeyGuid ? this.getTransactionFunctionKeys(this.errorFunctionKeyGuid, TradingEndHours, TradingEndMinute) : Promise.resolve([]),
    ]);

    const result: ErrorVoidAggregationResult = {
      Voids: voidTransactionFunctionKeys,
      Errors: errorTransactionFunctionKeys,
    }

    return result;
  }

  private async getError_VoidFunctionKeys(): Promise<SlimFunctionKey[]> {
    return await this.getApiDataGET<SlimFunctionKey>("SlimFunctionKeys")
  }

  private async getTransactionFunctionKeys(functionKeyGUID: Guid, tradingEndHours: number, tradingEndMinute: number): Promise<SlimTransactionFunctionKey[]> {
    const dateFrom = this.createDate(tradingEndHours, tradingEndMinute, new Date())

    return await this.getApiDataPOST<SlimTransactionFunctionKey>(`SlimTransactionFunctionKeys/${functionKeyGUID}`, dateFrom)
  }

  private createDate(tradingEndHours: number, tradingEndMinute: number, base: Date, offsetDays = 0): Date {
    const date = new Date(base);
    date.setHours(tradingEndHours, tradingEndMinute, 0, 0);

    if (base < date){
      offsetDays--;
    }

    date.setDate(date.getDate() + offsetDays);
    return date;
  }

  private async getApiDataPOST<T>(endpointAddendum: string, dateFrom: Date): Promise<T[]>{
    const Token: string = this.loginService.AccessToken.authToken;

    if (Token !== '') {
      // replacing siteId as usual + switch on v2 for taxes
      const endPoint: string = this.DashboardEndPoint.replace('{corporationId}', this.estateService.CorporationID).replace('{siteId}', this.estateService.SelectedSite.siteId) + endpointAddendum;

      return firstValueFrom(this.http.post(endPoint, dateFrom, { headers: new HttpHeaders().set('Authorization', 'Bearer ' + Token), observe: 'response' }).pipe(
        map((data: HttpResponse<T[]>) => {

          if (data.ok) {
            return data.body;
          }

        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        })
      ));
    }
  }

  private async getApiDataGET<T>(endpointAddendum: string): Promise<T[]>{
    const Token: string = this.loginService.AccessToken.authToken;

    if (Token !== '') {
      // replacing siteId as usual + switch on v2 for taxes
      const endPoint: string = this.DashboardEndPoint.replace('{corporationId}', this.estateService.CorporationID).replace('{siteId}', this.estateService.SelectedSite.siteId) + endpointAddendum;

      return firstValueFrom(this.http.get(endPoint, { headers: new HttpHeaders().set('Authorization', 'Bearer ' + Token), observe: 'response' }).pipe(
        map((data: HttpResponse<T[]>) => {

          if (data.ok) {
            return data.body;
          }

        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        })
      ));
    }
  }

  public async getResumeToken(endpointAddendum: string): Promise<ResumeToken>{
    const Token: string = this.loginService.AccessToken.authToken;

    if (Token !== '') {
      const endPoint: string = this.DashboardEndPoint.replace('{corporationId}', this.estateService.CorporationID).replace('{siteId}', this.estateService.SelectedSite.siteId) + 'Resume' + endpointAddendum;

      const headers = new HttpHeaders({
        'Authorization': 'Bearer ' + Token,
        'Content-Type': 'application/json'
      });

      return firstValueFrom(this.http.get(endPoint, { headers, observe: 'response' }).pipe(
        map((data: HttpResponse<ResumeToken>) => {

          if (data.ok) {
            return data.body;
          }

        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        })
      ));
    }
  }

  public async resumeChangeSince<T>(endpointAddendum: string, resumeToken: ResumeToken): Promise<ChangesSince<T>>{
    const Token: string = this.loginService.AccessToken.authToken;

    if (Token !== '') {
      const endPoint: string = this.DashboardEndPoint.replace('{corporationId}', this.estateService.CorporationID).replace('{siteId}', this.estateService.SelectedSite.siteId) + 'Resume' + endpointAddendum;

      const headers = new HttpHeaders({
        'Authorization': 'Bearer ' + Token,
        'Content-Type': 'application/json'
      });

      return firstValueFrom(this.http.post(endPoint, resumeToken, { headers, observe: 'response' }).pipe(
        map((data: HttpResponse<ChangesSince<T>>) => {

          if (data.ok) {
            return data.body;
          }

        }),
        catchError((err: HttpErrorResponse) => {
          return throwError(() => err);
        })
      ));
    }
  }
}
